import React from 'react';

import { faMinus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { type CreateMembershipGroupFormSchemaValues } from 'src/features/memberships/api/useCreateMembershipGroup';
import { ALLOWED_DAYS } from 'src/features/memberships/types';
import { allowedDaysOptions } from 'src/features/memberships/utils/constants';
import { membershipMessages } from 'src/features/memberships/utils/messages';
import { useListPoints } from 'src/features/points/api/useListPoints';
import { useListPointsGroup } from 'src/features/points/api/useListPointsGroup';
import { useListSubParkings } from 'src/features/subParkings/api/useListSubParkings';
import { useParking } from 'src/providers/ParkingProvider';
import { Button } from 'src/registry/ui/Button';
import { DatePickerWithRange } from 'src/registry/ui/DatePicker';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import {
    MultiSelect,
    MultiSelectContent,
    MultiSelectControl,
    MultiSelectOption,
    MultiSelectTrigger,
} from 'src/registry/ui/MultiSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { Textarea } from 'src/registry/ui/Textarea';
import { generalMessages } from 'src/utils/messages';

type MembershipGroupFormProps = {
    form: UseFormReturn<CreateMembershipGroupFormSchemaValues>;
    onSubmit: (values: CreateMembershipGroupFormSchemaValues) => void;
    modalMode?: 'edit' | 'copy' | 'create';
};

function MembershipGroupForm({ onSubmit, form, modalMode }: MembershipGroupFormProps) {
    const { parkingId } = useParking();
    const { formatMessage } = useIntl();

    const reservedPlaceTranslations = {
        [formatMessage(generalMessages.yes)]: 'true',
        [formatMessage(generalMessages.no)]: 'false',
    };

    const { data: points } = useListPoints({ parkingId, limit: 100 });
    const { data: subParkings } = useListSubParkings({ parkingId, limit: 100 });
    const { data: pointsGroup } = useListPointsGroup({ parkingId, limit: 100 });

    const handleAllowedDaysChange = (value: ALLOWED_DAYS) => {
        const allowedDays = form.getValues('allowed_days');
        if (allowedDays.includes(value)) {
            form.setValue(
                'allowed_days',
                // @ts-expect-error Weird ts behaviour, ignore for later
                allowedDays.filter((day) => day !== value),
                { shouldDirty: true },
            );
        } else {
            form.setValue('allowed_days', [...allowedDays, value], { shouldDirty: true });
        }
    };
    const selectedGroupPoint = form.watch('allowed_group_points');
    const selectedPointsBasedOnGroup = React.useMemo<string[]>(() => {
        const groupPoints = pointsGroup?.results.filter((point) => selectedGroupPoint?.includes(point.id));

        return groupPoints?.flatMap((point) => point.points) ?? [];
    }, [selectedGroupPoint, pointsGroup]);

    const prevGroupPointsRef = React.useRef<string[]>([]);

    React.useEffect(() => {
        if (modalMode !== 'edit' && modalMode !== 'copy') {
            if (selectedPointsBasedOnGroup && selectedPointsBasedOnGroup.length > 0) {
                form.setValue('allowed_points', selectedPointsBasedOnGroup);
            } else {
                form.setValue('allowed_points', []);
            }
        }

        if (modalMode === 'edit' || modalMode === 'copy') {
            const prevGroupPoints = prevGroupPointsRef.current;

            const groupsChanged =
                prevGroupPoints.length !== selectedPointsBasedOnGroup.length ||
                !selectedPointsBasedOnGroup.every((id) => prevGroupPoints.includes(id));

            if (!groupsChanged) return;
            const currentAllowedPoints = form.getValues('allowed_points') ?? [];

            const filteredAllowedPoints = currentAllowedPoints.filter((p) => !prevGroupPoints.includes(p));

            const mergedAllowedPoints = Array.from(new Set([...filteredAllowedPoints, ...selectedPointsBasedOnGroup]));

            form.setValue('allowed_points', mergedAllowedPoints, { shouldValidate: true });
            prevGroupPointsRef.current = selectedPointsBasedOnGroup;
        }
    }, [modalMode, selectedPointsBasedOnGroup, form]);

    const noGroupPointsData = pointsGroup?.count === 0;
    const groupPointsPlaceholder = noGroupPointsData
        ? membershipMessages.noMembershipPointGroupPlaceholder
        : membershipMessages.membershipPointGroupPlaceholder;

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(generalMessages.name)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    placeholder={formatMessage(membershipMessages.membershipNamePlaceholder)}
                                    value={field.value}
                                    onChange={field.onChange}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="valid_range"
                render={({ field }) => {
                    const { ref: _, ...rest } = field;
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                {formatMessage(membershipMessages.membershipDuration)}
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <DatePickerWithRange {...rest} />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />

            <FormField
                control={form.control}
                name="allowed_hours_start"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(membershipMessages.timeFrame)}
                        </FormLabel>
                        <div className="flex w-full items-center gap-4">
                            <FormControlStack>
                                <FormControl>
                                    <Input type="time" className="w-full" step={1} {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                            <FontAwesomeIcon className="h-10 w-3 text-sm text-gray-400" icon={faMinus} />
                            <FormField
                                control={form.control}
                                name="allowed_hours_end"
                                render={({ field }) => (
                                    <FormControlStack>
                                        <FormControl>
                                            <Input type="time" className="w-full" step={1} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                )}
                            />
                        </div>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="allowed_days"
                render={() => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(membershipMessages.allowedDays)}
                        </FormLabel>
                        <FormControlStack>
                            <div className="flex w-full flex-wrap gap-2">
                                {allowedDaysOptions.map((day) => (
                                    <Button
                                        onClick={() => handleAllowedDaysChange(day.value)}
                                        key={`option-${day.value}`}
                                        className="flex-1 px-2"
                                        variant={
                                            form.getValues('allowed_days').includes(day.value)
                                                ? 'default'
                                                : 'outline-solid'
                                        }
                                        type="button"
                                    >
                                        {formatMessage(day.label).substring(0, 3)}
                                    </Button>
                                ))}
                            </div>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="allowed_group_points"
                render={({ field }) => {
                    // Remove ref from field to avoid passing it to the MultiSelect component
                    const { ref: _, ...rest } = field;
                    const fieldData = field.value
                        ?.map((groupPoint) => pointsGroup?.results?.find((p) => p.id === groupPoint)?.name)
                        .join(', ');

                    const allValues = pointsGroup?.results?.map((point) => point.id);
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">
                                <FormattedMessage {...membershipMessages.allowedPointGroup} />
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <MultiSelect {...rest} value={field.value ?? []}>
                                        <MultiSelectTrigger asChild disabled={noGroupPointsData}>
                                            <MultiSelectControl>
                                                {fieldData}
                                                {fieldData?.length == 0 && (
                                                    <span className="text-gray-400">
                                                        {formatMessage(groupPointsPlaceholder)}
                                                    </span>
                                                )}
                                            </MultiSelectControl>
                                        </MultiSelectTrigger>
                                        <MultiSelectContent allValues={allValues}>
                                            {pointsGroup?.results.map((point) => (
                                                <MultiSelectOption key={point.id} selected={point.id}>
                                                    {point.name}
                                                </MultiSelectOption>
                                            ))}
                                        </MultiSelectContent>
                                    </MultiSelect>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />

            <FormField
                control={form.control}
                name="allowed_points"
                render={({ field }) => {
                    // Remove ref from field to avoid passing it to the MultiSelect component
                    const { ref: _, ...rest } = field;

                    const fieldData =
                        field.value
                            ?.map((id) => points?.results.find((p) => p.id === id)?.name)
                            .filter(Boolean)
                            .join(', ') ?? '';

                    const allValues = points?.results.map((point) => point.id);
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                <FormattedMessage {...membershipMessages.allowedPoints} />
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <MultiSelect
                                        {...rest}
                                        onChange={(values) => {
                                            field.onChange(values);
                                        }}
                                    >
                                        <MultiSelectTrigger asChild>
                                            <MultiSelectControl>
                                                {fieldData}
                                                {fieldData.length == 0 && (
                                                    <span className="text-gray-400">
                                                        <FormattedMessage
                                                            {...membershipMessages.membershipPointsPlaceholder}
                                                        />
                                                    </span>
                                                )}
                                            </MultiSelectControl>
                                        </MultiSelectTrigger>
                                        <MultiSelectContent allValues={allValues}>
                                            {points?.results.map((point) => (
                                                <MultiSelectOption key={point.id} selected={point.id}>
                                                    {point.name}
                                                </MultiSelectOption>
                                            ))}
                                        </MultiSelectContent>
                                    </MultiSelect>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />

            <FormField
                control={form.control}
                name="allowed_parkings"
                render={({ field }) => {
                    // Remove ref from field to avoid passing it to the MultiSelect component
                    const { ref: _, ...rest } = field;
                    const fieldData = field.value
                        .map((subParking) => subParkings?.results.find((p) => p.id === subParking)?.name)
                        .join(', ');

                    const allValues = subParkings?.results.map((point) => point.id);
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                <FormattedMessage {...membershipMessages.allowedParkings} />
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <MultiSelect {...rest}>
                                        <MultiSelectTrigger asChild>
                                            <MultiSelectControl>
                                                {fieldData}
                                                {fieldData.length == 0 && (
                                                    <span className="text-gray-400">
                                                        <FormattedMessage
                                                            {...membershipMessages.allowedParkingsPlaceholder}
                                                        />
                                                    </span>
                                                )}
                                            </MultiSelectControl>
                                        </MultiSelectTrigger>
                                        <MultiSelectContent allValues={allValues}>
                                            {subParkings?.results.map((point) => (
                                                <MultiSelectOption key={point.id} selected={point.id}>
                                                    {point.name}
                                                </MultiSelectOption>
                                            ))}
                                        </MultiSelectContent>
                                    </MultiSelect>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />

            <FormField
                control={form.control}
                name="allowed_parking_places"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(membershipMessages.allowedParkingPlaces)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input type="number" placeholder="1" min={0} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="reserve_place"
                render={({ field }) => {
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">{formatMessage(membershipMessages.reservedPlace)}</FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Select
                                        onValueChange={(val) => field.onChange(val === 'true')}
                                        value={field.value === undefined ? '' : String(field.value)}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue
                                                placeholder={formatMessage(membershipMessages.reservedPlace)}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(reservedPlaceTranslations).map(([key, value]) => {
                                                return (
                                                    <SelectItem key={key} value={value}>
                                                        {key}
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
            <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(membershipMessages.description)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Textarea
                                    placeholder={formatMessage(membershipMessages.description)}
                                    {...field}
                                    value={field.value ?? ''}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </Form>
    );
}

export { MembershipGroupForm };
